import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Download, Loader2, Paperclip, Pencil, Plus, Trash2 } from 'lucide-react'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@quebon/ui/components/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { Input } from '@quebon/ui/components/input'
import { Textarea } from '@quebon/ui/components/textarea'
import { handleApiError } from '@/api/client'
import {
  createConsultation,
  createTask,
  deleteAttachment,
  fetchAttachmentDownloadUrl,
  fetchAttachments,
  fetchConsultations,
  fetchTasks,
  updateConsultation,
  updateTaskStatus,
  uploadAttachment,
  type ConsultationListItem,
  type TaskListItem,
} from '@/api/lcCrm'
import {
  CONSULTATION_CHANNEL_LABEL,
  TASK_STATUS_LABEL,
  formatKstDateTime,
  labelOf,
} from './crmLabels'
import { ErrorBlock, LabeledSelect, LoadingBlock } from './crmComponents'

/**
 * FR-24 상담 4항목 · 첨부 · 할 일.
 *
 * 상담의 `taskText`를 채우면 서버가 파생 할 일을 함께 만든다. 화면은 그 관계를 드러낸다 —
 * 할 일 목록에 상담에서 나온 것과 직접 만든 것이 섞이면 어디를 고쳐야 하는지 알 수 없다.
 */
export function LeadActivityTab({ leadId }: { leadId: number }) {
  const [consultOpen, setConsultOpen] = useState(false)
  const [taskOpen, setTaskOpen] = useState(false)

  const consultations = useQuery({
    queryKey: ['lc-crm-consultations', leadId],
    queryFn: () => fetchConsultations(leadId),
  })
  const tasks = useQuery({
    queryKey: ['lc-crm-tasks', leadId],
    queryFn: () => fetchTasks(leadId),
  })

  return (
    <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
      <Card>
        <CardHeader>
          <CardTitle>상담 기록</CardTitle>
          <CardDescription>문의 · 답변 · 미해결 · 할 일 네 항목으로 남깁니다.</CardDescription>
          <Button className="justify-self-end" size="sm" onClick={() => setConsultOpen(true)}>
            <Plus className="size-4" />
            상담 추가
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {consultations.isLoading && <LoadingBlock />}
          {consultations.error && <ErrorBlock message="상담 기록을 불러오지 못했습니다." />}
          {consultations.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">상담 기록이 없습니다.</p>
          )}
          {consultations.data?.map((item) => (
            <ConsultationCard key={item.consultationId} consultation={item} leadId={leadId} />
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>할 일</CardTitle>
          <CardDescription>상담에서 파생된 것과 직접 만든 것이 함께 보입니다.</CardDescription>
          <Button className="justify-self-end" size="sm" variant="outline" onClick={() => setTaskOpen(true)}>
            <Plus className="size-4" />
            할 일 추가
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {tasks.isLoading && <LoadingBlock />}
          {tasks.error && <ErrorBlock message="할 일을 불러오지 못했습니다." />}
          {tasks.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">할 일이 없습니다.</p>
          )}
          {tasks.data?.map((task) => (
            <TaskRow key={task.taskId} task={task} leadId={leadId} />
          ))}
        </CardContent>
      </Card>

      {consultOpen && (
        <ConsultationDialog leadId={leadId} onClose={() => setConsultOpen(false)} />
      )}
      {taskOpen && <TaskDialog leadId={leadId} onClose={() => setTaskOpen(false)} />}
    </div>
  )
}

function ConsultationCard({
  consultation,
  leadId,
}: {
  consultation: ConsultationListItem
  leadId: number
}) {
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [editOpen, setEditOpen] = useState(false)

  const attachments = useQuery({
    queryKey: ['lc-crm-attachments', consultation.consultationId],
    queryFn: () => fetchAttachments(consultation.consultationId),
  })

  const upload = useMutation({
    mutationFn: (file: File) => uploadAttachment(consultation.consultationId, file),
    onSuccess: () => {
      toast.success('첨부를 올렸습니다')
      queryClient.invalidateQueries({ queryKey: ['lc-crm-attachments', consultation.consultationId] })
    },
    onError: (err) => toast.error(handleApiError(err) || '첨부 업로드 실패'),
  })

  const remove = useMutation({
    mutationFn: (attachmentId: number) => deleteAttachment(attachmentId),
    onSuccess: () => {
      toast.success('첨부를 삭제했습니다')
      queryClient.invalidateQueries({ queryKey: ['lc-crm-attachments', consultation.consultationId] })
    },
    onError: (err) => toast.error(handleApiError(err) || '첨부 삭제 실패'),
  })

  const download = useMutation({
    mutationFn: (attachmentId: number) => fetchAttachmentDownloadUrl(attachmentId),
    onSuccess: ({ downloadUrl }) => {
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    },
    onError: (err) => toast.error(handleApiError(err) || '내려받기 URL 발급 실패'),
  })

  const live = attachments.data?.filter((item) => !item.logicalDeletedAt) ?? []

  return (
    <article className="flex flex-col gap-2 rounded-lg border border-border p-3">
      <header className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">{labelOf(CONSULTATION_CHANNEL_LABEL, consultation.channel)}</Badge>
        <span className="text-xs text-muted-foreground">
          {formatKstDateTime(consultation.occurredAt)} · 작성 #{consultation.authorUserId}
        </span>
        {consultation.derivedTaskId && (
          <Badge variant="outline">할 일 #{consultation.derivedTaskId}</Badge>
        )}
        <Button
          className="ml-auto"
          size="xs"
          variant="ghost"
          aria-label={`상담 ${consultation.consultationId} 수정`}
          onClick={() => setEditOpen(true)}
        >
          <Pencil className="size-3" />
          수정
        </Button>
      </header>

      <dl className="flex flex-col gap-1.5 text-sm">
        <ConsultationField label="문의" value={consultation.inquiryText} />
        <ConsultationField label="답변" value={consultation.answerText} />
        <ConsultationField label="미해결" value={consultation.unansweredText} />
        <ConsultationField label="할 일" value={consultation.taskText} />
      </dl>

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-2">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) upload.mutate(file)
            e.target.value = ''
          }}
        />
        <Button
          size="xs"
          variant="ghost"
          disabled={upload.isPending}
          onClick={() => fileRef.current?.click()}
        >
          {upload.isPending ? <Loader2 className="size-3 animate-spin" /> : <Paperclip className="size-3" />}
          첨부 추가
        </Button>
        {live.map((item) => (
          <span key={item.attachmentId} className="flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs">
            {item.originalFilename}
            <Button
              size="icon-xs"
              variant="ghost"
              aria-label={`${item.originalFilename} 내려받기`}
              disabled={download.isPending}
              onClick={() => download.mutate(item.attachmentId)}
            >
              <Download className="size-3" />
            </Button>
            <Button
              size="icon-xs"
              variant="ghost"
              aria-label={`${item.originalFilename} 삭제`}
              disabled={remove.isPending}
              onClick={() => remove.mutate(item.attachmentId)}
            >
              <Trash2 className="size-3" />
            </Button>
          </span>
        ))}
        {live.length === 0 && !upload.isPending && (
          <span className="text-xs text-muted-foreground">첨부 없음</span>
        )}
      </div>
      <span className="sr-only">리드 {leadId}</span>
      {editOpen && (
        <ConsultationDialog leadId={leadId} initial={consultation} onClose={() => setEditOpen(false)} />
      )}
    </article>
  )
}

function ConsultationField({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div className="flex gap-2">
      <dt className="w-12 shrink-0 text-muted-foreground">{label}</dt>
      <dd className="whitespace-pre-wrap">{value}</dd>
    </div>
  )
}

function TaskRow({ task, leadId }: { task: TaskListItem; leadId: number }) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (status: string) => updateTaskStatus(task.taskId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lc-crm-tasks', leadId] })
      queryClient.invalidateQueries({ queryKey: ['lc-crm-dashboard'] })
    },
    onError: (err) => toast.error(handleApiError(err) || '할 일 상태 변경 실패'),
  })

  return (
    <div className="flex items-start justify-between gap-3 border-b border-border pb-3 last:border-b-0 last:pb-0">
      <div className="flex flex-col gap-1">
        <span className="text-sm">{task.taskText}</span>
        <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <Badge variant={task.status === 'OPEN' ? 'default' : 'outline'}>
            {labelOf(TASK_STATUS_LABEL, task.status)}
          </Badge>
          {task.dueDate && <span className="tabular-nums">기한 {task.dueDate}</span>}
          {task.assignedTeam && <span>{task.assignedTeam}</span>}
          {task.consultationId && <span>상담 #{task.consultationId}</span>}
        </span>
      </div>
      <LabeledSelect
        size="sm"
        className="w-28"
        ariaLabel={`할 일 ${task.taskId} 상태`}
        value={task.status}
        onChange={(next) => {
          if (next !== task.status) mutation.mutate(next)
        }}
        items={Object.entries(TASK_STATUS_LABEL).map(([value, label]) => ({ value, label }))}
      />
    </div>
  )
}

/**
 * 상담 기록·수정 다이얼로그. `initial`이 있으면 수정이다.
 *
 * 이관된 상담이든 직접 적은 것이든 같은 화면에서 고친다 (2026-09-07 QA — 가져온 상담 기록을 고칠 수
 * 없었다). 수정은 바뀐 필드만 보낸다. 할 일 문구를 비우면 `clearTask`로 보내 서버가 파생 할 일을
 * 취소하고, 바꾸면 OPEN 파생 할 일을 갱신하거나 이미 끝난 것이면 새로 만든다.
 */
function ConsultationDialog({
  leadId,
  initial,
  onClose,
}: {
  leadId: number
  initial?: ConsultationListItem
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const editing = initial != null
  const [channel, setChannel] = useState(initial?.channel ?? 'PHONE')
  const [inquiry, setInquiry] = useState(initial?.inquiryText ?? '')
  const [answer, setAnswer] = useState(initial?.answerText ?? '')
  const [unanswered, setUnanswered] = useState(initial?.unansweredText ?? '')
  const [taskText, setTaskText] = useState(initial?.taskText ?? '')
  const [taskTeam, setTaskTeam] = useState('')
  const [taskDue, setTaskDue] = useState('')

  const mutation = useMutation({
    mutationFn: () => {
      if (!initial) {
        return createConsultation(leadId, {
          channel,
          inquiryText: inquiry || undefined,
          answerText: answer || undefined,
          unansweredText: unanswered || undefined,
          taskText: taskText || undefined,
          taskAssignedTeam: taskTeam || undefined,
          taskDueDate: taskDue || undefined,
        })
      }
      const changed = <T,>(next: T, prev: T | null | undefined) => (next === (prev ?? '') ? undefined : next)
      const nextTask = taskText.trim()
      const hadTask = Boolean(initial.taskText)
      const clearTask = hadTask && nextTask === ''
      const taskChanged = nextTask !== '' && nextTask !== (initial.taskText ?? '')
      return updateConsultation(initial.consultationId, {
        channel: changed(channel, initial.channel),
        inquiryText: changed(inquiry, initial.inquiryText),
        answerText: changed(answer, initial.answerText),
        unansweredText: changed(unanswered, initial.unansweredText),
        taskText: taskChanged ? nextTask : undefined,
        clearTask: clearTask || undefined,
        taskAssignedTeam: taskChanged && taskTeam ? taskTeam : undefined,
        taskDueDate: taskChanged && taskDue ? taskDue : undefined,
      })
    },
    onSuccess: () => {
      toast.success(editing ? '상담을 수정했습니다' : '상담을 기록했습니다')
      queryClient.invalidateQueries({ queryKey: ['lc-crm-consultations', leadId] })
      queryClient.invalidateQueries({ queryKey: ['lc-crm-tasks', leadId] })
      queryClient.invalidateQueries({ queryKey: ['lc-crm-lead', leadId] })
      onClose()
    },
    onError: (err) => toast.error(handleApiError(err) || (editing ? '상담 수정 실패' : '상담 기록 실패')),
  })

  const empty = !inquiry.trim() && !answer.trim() && !unanswered.trim() && !taskText.trim()
  const taskTouched = editing && taskText.trim() !== '' && taskText.trim() !== (initial.taskText ?? '')

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? '상담 수정' : '상담 기록'}</DialogTitle>
          <DialogDescription>
            {editing
              ? '할 일 항목을 비우면 파생 할 일이 취소되고, 바꾸면 진행 중인 파생 할 일이 갱신됩니다.'
              : '할 일 항목을 채우면 같은 내용의 할 일이 함께 만들어집니다.'}
          </DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field>
            <FieldLabel>채널</FieldLabel>
            <LabeledSelect
              ariaLabel="채널"
              value={channel}
              onChange={setChannel}
              items={Object.entries(CONSULTATION_CHANNEL_LABEL).map(([value, label]) => ({ value, label }))}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="cons-inquiry">문의</FieldLabel>
            <Textarea id="cons-inquiry" rows={2} value={inquiry} onChange={(e) => setInquiry(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel htmlFor="cons-answer">답변</FieldLabel>
            <Textarea id="cons-answer" rows={2} value={answer} onChange={(e) => setAnswer(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel htmlFor="cons-unanswered">미해결</FieldLabel>
            <Textarea id="cons-unanswered" rows={2} value={unanswered} onChange={(e) => setUnanswered(e.target.value)} />
          </Field>
          <Field>
            <FieldLabel htmlFor="cons-task">할 일</FieldLabel>
            <Textarea id="cons-task" rows={2} value={taskText} onChange={(e) => setTaskText(e.target.value)} />
            <FieldDescription>
              {editing ? '비우면 파생 할 일이 취소됩니다.' : '채우면 파생 할 일이 함께 생성됩니다.'}
            </FieldDescription>
          </Field>
          {(editing ? taskTouched : Boolean(taskText.trim())) && (
            <div className="grid grid-cols-2 gap-4">
              <Field>
                <FieldLabel htmlFor="cons-task-team">할 일 담당팀</FieldLabel>
                <Input id="cons-task-team" value={taskTeam} onChange={(e) => setTaskTeam(e.target.value)} />
              </Field>
              <Field>
                <FieldLabel htmlFor="cons-task-due">할 일 기한</FieldLabel>
                <Input id="cons-task-due" type="date" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} />
              </Field>
            </div>
          )}
        </FieldGroup>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button disabled={empty || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            {editing ? '저장' : '기록'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function TaskDialog({ leadId, onClose }: { leadId: number; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [text, setText] = useState('')
  const [team, setTeam] = useState('')
  const [due, setDue] = useState('')

  const mutation = useMutation({
    mutationFn: () =>
      createTask(leadId, {
        taskText: text,
        assignedTeam: team || undefined,
        dueDate: due || undefined,
      }),
    onSuccess: () => {
      toast.success('할 일을 만들었습니다')
      queryClient.invalidateQueries({ queryKey: ['lc-crm-tasks', leadId] })
      onClose()
    },
    onError: (err) => toast.error(handleApiError(err) || '할 일 생성 실패'),
  })

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>할 일 추가</DialogTitle>
          <DialogDescription>상담과 무관하게 직접 만드는 할 일입니다.</DialogDescription>
        </DialogHeader>

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="task-text">내용</FieldLabel>
            <Textarea id="task-text" rows={3} value={text} onChange={(e) => setText(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="task-team">담당팀</FieldLabel>
              <Input id="task-team" value={team} onChange={(e) => setTeam(e.target.value)} />
            </Field>
            <Field>
              <FieldLabel htmlFor="task-due">기한</FieldLabel>
              <Input id="task-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
            </Field>
          </div>
        </FieldGroup>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button disabled={!text.trim() || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            추가
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
